import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, BookOpen } from 'lucide-react';

const AcademicPreparation: React.FC = () => {
    const navigate = useNavigate();

    const handleContactClick = () => {
        navigate('/eligibility');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20">
            <div className="bg-[#009B8F] text-white py-16 mb-16">
                <div className="max-w-7xl mx-auto px-6">
                    <h1 className="text-4xl md:text-5xl font-serif mb-4">Academic Preparation</h1>
                    <p className="text-xl text-white/90 max-w-2xl">
                        Equipping students with the academic mindset, skills, and strategies for global success.
                    </p>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6">
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-[#009B8F]/10 rounded-full">
                        <BookOpen className="text-[#009B8F] w-8 h-8" />
                    </div>
                    <h2 className="text-3xl font-serif text-[#011E26]">Foundational Excellence</h2>
                </div>

                <div className="prose prose-lg text-slate-600 max-w-none">
                    <p className="mb-4">
                        Academic Preparation is a foundational step in your study abroad journey that goes beyond simple test coaching.
                        At Nex Overseas, we equip students with the academic mindset, skills, and strategies they need to thrive in competitive global education environments.
                    </p>
                    <p className="mb-4">
                        In today’s international admissions landscape, universities don’t just look at test scores — they assess your academic readiness, adaptability, logical thinking, and preparedness for higher-level coursework.
                        Our Academic Preparation service is designed to develop all of these elements with a personalized, student-centric approach.
                    </p>
                    <p className="mb-4">
                        We begin by assessing your current academic profile to identify strengths and areas for improvement. This forms the basis of a tailored preparation plan that includes:
                    </p>

                    <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-[#009B8F] mb-8">
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[
                                "Advanced subject foundation building",
                                "Critical thinking exercises",
                                "Research skills enhancement",
                                "Academic writing discipline",
                                "Structured practice simulating real classroom expectations"
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm font-medium">
                                    <CheckCircle className="w-5 h-5 text-[#009B8F] flex-shrink-0" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <p className="mb-4">
                        What sets our Academic Preparation apart is our focus on future-ready learners — students who can not only gain admission but also excel academically once enrolled.
                        Our program also trains students in time management, reading comprehension, analytical reasoning, and academic communication strategies, empowering you to confidently tackle rigorous study demands.
                    </p>

                    <h3 className="text-2xl font-serif text-[#011E26] mb-4 mt-8">Our Services Include:</h3>
                    <ul className="list-disc pl-6 mb-6 space-y-2">
                        <li>Personalized academic assessment and planning</li>
                        <li>Subject-specific preparation and skill development</li>
                        <li>Research and writing skills coaching</li>
                        <li>Critical thinking and problem-solving workshops</li>
                        <li>Exam strategy and practice sessions</li>
                    </ul>

                    <p className="mb-8">
                        Whether you are preparing to meet entry requirements for top universities or want to sharpen your academic abilities before departure, our coaching blends personalised attention with global best practices — ensuring higher academic performance and smoother transitions into your chosen programs.
                    </p>

                    <p className="mb-8 font-medium text-[#011E26]">
                        Get in touch today to learn more about our Academic Preparation services and start your journey toward global academic success.
                    </p>

                    <button
                        onClick={handleContactClick}
                        className="bg-[#009B8F] text-white px-8 py-3 font-bold uppercase tracking-widest hover:bg-[#00857a] transition-colors rounded-sm shadow-lg"
                    >
                        Contact Us / Call Now
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AcademicPreparation;
